import React, { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../Components/ui/select";
import Default_WhatsApp_background from "../../../Assets/Default_WhatsApp_background.jpeg";
import { FaFilePdf } from "react-icons/fa";
import { revertCampaignWindowFromUTC } from "../../../Pages/Campaign/CreateCampaign/utils/revertCampaignWindowFromUTC";

export interface OperatorKey {
  channel: string;
  country: string;
  operatorId: string | number;
  label: string;
}

interface RowData {
  buttonType: string;
  buttonText: string;
  buttonTypeDropdown: string;
  websiteUrl: string;
  countryCode: string;
  callPhoneNumber: string;
  copyOfferCode: string;
}

interface OperatorMessagePreviewCardProps {
  operatorKeys: OperatorKey[];
  selectedOperatorKey: OperatorKey | null;
  setSelectedOperatorKey: (key: OperatorKey) => void;
  operatorTemplates: Record<string, Record<string, Record<string, any>>>;
  allOperatorKeys?: OperatorKey[];
}

const OperatorMessagePreviewCard: React.FC<OperatorMessagePreviewCardProps> = ({
  operatorKeys,
  selectedOperatorKey,
  setSelectedOperatorKey,
  operatorTemplates,
  allOperatorKeys,
}) => {
  const [bodyText, setBodyText] = useState<string>("");
  const [FooterText, setFooterText] = useState<string>("");
  const [buttonText, setButtonText] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [textInput, setTextInput] = useState<string>("");
  const [rows, setRows] = useState<RowData[]>([]);
  const [channelType, setChannelType] = useState<"SMS" | "WhatsApp" | null>(
    null
  );

  // Use allOperatorKeys if provided (for global preview), fallback to operatorKeys
  const dropdownOperatorKeys = allOperatorKeys || operatorKeys;

  useEffect(() => {
    if (!selectedOperatorKey) {
      setSelectedOption("");
      setTextInput("");
      setBodyText("");
      setFooterText("");
      setRows([]);
      setChannelType(null);
      return;
    }
    const { channel, country, operatorId } = selectedOperatorKey;
    let template = operatorTemplates?.[channel]?.[country]?.[operatorId];
    if (!template) {
      setSelectedOption("");
      setTextInput("");
      setBodyText("");
      setFooterText("");
      setRows([]);
      setChannelType(null);
      return;
    }

    // --- Unwrap WhatsApp template (meta/custom) ---
    if (template.template && Array.isArray(template.template.components)) {
      template = template.template;
      setChannelType("WhatsApp");
    }
    // --- SMS ---
    if (template.message || (template.template && template.template.message)) {
      setChannelType("SMS");
      setBodyText(template.message ?? template.template?.message ?? "");
      setSelectedOption("");
      setTextInput("");
      setFooterText("");
      setRows([]);
      return;
    }

    // --- WhatsApp components ---
    setChannelType("WhatsApp");
    const headerComponent = template.components?.find(
      (c: any) => c.type === "HEADER"
    );
    setSelectedOption(headerComponent?.format?.toLowerCase() || "");
    setTextInput(headerComponent?.text || "");

    const bodyComponent = template.components?.find(
      (c: any) => c.type === "BODY"
    );
    setBodyText(bodyComponent?.text || "");

    const footerComponent = template.components?.find(
      (c: any) => c.type === "FOOTER"
    );
    setFooterText(footerComponent?.text || "");

    const buttonsComponent = template.components?.find(
      (c: any) => c.type === "BUTTONS"
    );
    if (buttonsComponent && buttonsComponent.buttons) {
      const formattedButtons = buttonsComponent.buttons
        .map((btn: any) => {
          if (btn.type === "URL") {
            return {
              buttonType: "View Website",
              buttonText: btn.text, // WA URL buttons usually have text
              websiteUrl: btn.url || "",
            };
          }
          if (btn.type === "PHONE_NUMBER") {
            return {
              buttonType: "Call Phone Number",
              buttonText: btn.text, // label, if present
              callPhoneNumber: btn.phone_number || "",
            };
          }
          if (btn.type === "QUICK_REPLY") {
            return {
              buttonType: "Copy Offer Code",
              buttonText: btn.text || "Copy", // label if provided
              copyOfferCode: "", // QUICK_REPLY doesn't have code by default
            };
          }
          if (btn.type === "COPY_CODE") {
            return {
              buttonType: "COPY_CODE", // keep exact type so we can style/handle if needed
              buttonText: btn.text || "Copy",
              copyOfferCode: btn.example || "", // the actual code to copy
              websiteUrl: "",
              countryCode: "",
              callPhoneNumber: "",
              buttonTypeDropdown: "",
            } as RowData;
          }
          return null;
        })
        .filter(Boolean) as RowData[];

      setRows(formattedButtons);
    } else {
      setRows([]);
    }
  }, [selectedOperatorKey, operatorTemplates]);

  // ---- UI Helpers ----
  const textAreaIcon = () => (
    <div className="relative w-8 h-8 flex justify-center items-center">
      <div
        className="absolute rounded w-[3px] h-8 transform rotate-45"
        style={{ borderLeft: "3px solid #64748B" }}
      />
      <div
        className="absolute w-[3px] h-4 rounded transform rotate-45 translate-x-2 translate-y-2"
        style={{ borderLeft: "3px solid #64748B" }}
      />
    </div>
  );
  const renderFormattedText = (text: string) =>
    text
      .replace(/\*([^*]+)\*/g, "<strong>$1</strong>")
      .replace(/_([^_]+)_/g, "<em>$1</em>")
      .replace(/~([^~]+)~/g, "<s>$1</s>")
      .replace(
        /`([^`]+)`/g,
        '<code style="background: #f1f1f1; padding: 2px 4px; border-radius: 3px;">$1</code>'
      )
      .replace(
        /\{\{(\d+)\}\}/g,
        '<span style="background: #e3f2fd; padding: 2px 4px; border-radius: 3px; color: #1976d2;">{{$1}}</span>'
      );

  // SMS formatting for bold/italic
  const formatText = (text: string) =>
    text
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(/~(.*?)~/g, "<s>$1</s>")
      .replace(/`(.*?)`/g, "<code>$1</code>");

  // --- Main Preview UI ---
  const renderPreview = () => {
    const isOperatorAllocated = !!selectedOperatorKey;
    const isBodyEmpty = !bodyText?.trim();
    const isFooterEmpty = !FooterText?.trim();
    const isbuttonTextEmpty = !buttonText?.trim();
    const noContentSelected =
      !isOperatorAllocated ||
      (channelType === "SMS"
        ? isBodyEmpty
        : !selectedOption && !selectedFile && isBodyEmpty && isFooterEmpty);
    const channel = selectedOperatorKey ? selectedOperatorKey.channel : "";

    // --- SMS Preview ---
    if (channelType === "SMS") {
      return (
        <div className="flex flex-col h-full w-full bg-[#F0F4F8] rounded-2xl p-4 overflow-y-auto">
          {noContentSelected ? (
            <div className="flex flex-col items-center justify-center h-full w-full text-center">
              {textAreaIcon()}
              <div className="mt-6">
                <p className="text-xl font-semibold">Mobile screen</p>
              </div>
              <div
                className="w-[125px] mt-2"
                style={{ fontWeight: 500, fontSize: "14px" }}
              >
                <p>Preview varies based on platform selection</p>
              </div>
            </div>
          ) : (
            <div className="w-full rounded p-0 mt-0 min-h-fit text-left">
              <div
                className="whitespace-pre-wrap break-words text-sm text-[#020617]"
                dangerouslySetInnerHTML={{ __html: formatText(bodyText) }}
              />
            </div>
          )}
        </div>
      );
    }

    // --- WhatsApp Preview ---
    const whatsappStyle =
      channel?.toLowerCase() === "whatsapp" && !noContentSelected
        ? {
            backgroundImage: `url(${Default_WhatsApp_background})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundColor: "#f0f0f0",
          }
        : {
            backgroundColor: "#e5e7eb",
          };

    return (
      <div
        className="flex rounded-[20px] flex-col justify-between w-full h-auto bottom-0 p-4 overflow-hidden"
        style={whatsappStyle}
      >
        {noContentSelected ? (
          <div className="flex w-full h-[540px] items-center justify-center">
            <div className="text-center">
              {textAreaIcon()}
              <div className="mt-6">
                <p className="text-xl font-semibold">Mobile screen</p>
              </div>
              <div
                className="w-[125px] mt-2"
                style={{ fontWeight: 500, fontSize: "14px" }}
              >
                <p>Preview varies based on platform selection</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-center">
              {selectedOption === "image" &&
                (operatorTemplates?.[selectedOperatorKey!.channel]?.[
                  selectedOperatorKey!.country
                ]?.[selectedOperatorKey!.operatorId].media_config
                  ?.binary_data ? (
                  <img
                    src={
                      operatorTemplates[selectedOperatorKey!.channel][
                        selectedOperatorKey!.country
                      ][selectedOperatorKey!.operatorId].media_config
                        ?.binary_data
                    }
                    alt="Header"
                    className="w-full h-40 mt-2 object-cover mx-auto rounded-lg"
                  />
                ) : (
                  <div className="text-gray-400 text-center w-full h-[200px] flex items-center justify-center">
                    No image uploaded
                  </div>
                ))}
              {selectedOption === "video" &&
                (operatorTemplates?.[selectedOperatorKey!.channel]?.[ 
                  selectedOperatorKey!.country
                ]?.[selectedOperatorKey!.operatorId].media_config
                  ?.binary_data ? (
                  <video
                    controls
                    className="w-full h-40 mt-2 object-cover mx-auto rounded-lg"
                  >
                    <source
                      src={
                        operatorTemplates[selectedOperatorKey!.channel][
                          selectedOperatorKey!.country
                        ][selectedOperatorKey!.operatorId].media_config
                          .binary_data
                      }
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <div className="text-gray-400 text-center w-full h-[200px] flex items-center justify-center">
                    No video uploaded
                  </div>
                ))}
              {selectedOption === "document" &&
                operatorTemplates?.[selectedOperatorKey!.channel]?.[
                  selectedOperatorKey!.country
                ]?.[selectedOperatorKey!.operatorId]?.media_config
                  ?.file_type === "application/pdf" &&
                operatorTemplates[selectedOperatorKey!.channel][
                  selectedOperatorKey!.country
                ][selectedOperatorKey!.operatorId].media_config
                  ?.binary_data && (
                  <div className="mb-2 p-3 bg-gray-100 rounded-lg flex items-center space-x-2">
                    <FaFilePdf className="w-6 h-6 text-red-500" />
                    <div>
                      <div className="text-sm font-medium">
                        {
                          operatorTemplates[selectedOperatorKey!.channel][
                            selectedOperatorKey!.country
                          ][selectedOperatorKey!.operatorId].media_config
                            .file_name
                        }
                      </div>
                      <div className="text-xs text-gray-500">
                        {(
                          Number(
                            operatorTemplates[selectedOperatorKey!.channel][
                              selectedOperatorKey!.country
                            ][selectedOperatorKey!.operatorId].media_config
                              .file_length
                          ) / 1024
                        ).toFixed(1)}{" "}
                        KB
                      </div>
                    </div>
                  </div>
                )}
            </div>

            <div className="flex-1 text-black w-full text-left">
              {/* Header */}
              {selectedOption === "text" && textInput && (
                <p
                  className="w-full text-base font-sans p-2 font-bold"
                  style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
                >
                  {textInput}
                </p>
              )}

              {/* Body */}
              {!isBodyEmpty && (
                <div
                  className="mb-2 whitespace-pre-wrap break-words text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: renderFormattedText(bodyText),
                  }}
                />
              )}
            </div>

            <div className="border-t mt-2 pt-2 text-center">
              {!isFooterEmpty && (
                <p
                  className="font-semibold text-black"
                  style={{ fontSize: "14px" }}
                >
                  {FooterText}
                </p>
              )}
            </div>

            {/* Buttons area — identical structure/style to MessagePreviewCard.tsx */}
            <div className="border-t mt-2 pt-2 pb-2 text-center space-y-1">
              {rows.map((row, index) => (
                <div key={index} className="border-b border-gray-200 last:border-b-0 pb-1">
                  <p className="font-serif text-blue-400" style={{ fontSize: "14px" }}>
                    {row.buttonType === "View Website" ? (
                      <a
                        href={row.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {row.buttonText || "View Website"}
                      </a>
                    ) : (
                      // For COPY_CODE / QUICK_REPLY / PHONE_NUMBER etc., show same visual as MessagePreviewCard
                      // (plain blue text). If you want copy-to-clipboard behavior behind the scenes, you can
                      // optionally add an onClick here—but visually it stays the same.
                      row.buttonText || "Button"
                    )}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // --------- Main Render ----------
  return (
    <div className="w-[400px] space-y-3">
      <Select
        value={selectedOperatorKey ? JSON.stringify(selectedOperatorKey) : ""}
        onValueChange={(v) => setSelectedOperatorKey(JSON.parse(v))}
      >
        <SelectTrigger className="w-full bg-[#F8FAFC] text-[#020617] h-[32px] px-2 text-sm">
          <SelectValue placeholder="Select Preview" />
        </SelectTrigger>
        <SelectContent>
          {dropdownOperatorKeys.length === 0 ? (
            <SelectItem
              value="__no_operator__"
              disabled
              className="text-sm text-[#64748B] justify-center"
            >
              No operators allocated
            </SelectItem>
          ) : (
            dropdownOperatorKeys.map((key: OperatorKey) => (
              <SelectItem
                key={key.operatorId + "-" + key.country + "-" + key.channel}
                value={JSON.stringify(key)}
                className="text-sm"
              >
                {key.label}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {/* No outer card—just preview panel */}
      <div className="border rounded-lg p-3 w-[400px] h-auto overflow-hidden flex flex-col">
        <h2 className="text-sm font-semibold text-left text-[#020617] mb-3">
          Message preview
        </h2>
        <div className="flex flex-col items-center rounded-[20px] overflow-hidden flex-1">
          {renderPreview()}
        </div>
      </div>
    </div>
  );
};

export default OperatorMessagePreviewCard;
